import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import {
  EditButton,
  Show,
  Tab,
  TabbedShowLayout,
  useRecordContext,
  useRefresh,
} from "react-admin";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import CouponSkuMetaShow from "./CouponSkuMetaShow";
import { CouponSkuService } from "../../services/CouponSkuService";
import { WorkspaceContainer } from "../../styles/commonStyles";

const couponSkuService = new CouponSkuService();

const CustomButton = (props: any) => {
  const record = useRecordContext();
  return (
    <Button
      onClick={() => props.callback(record)}
      size="small"
      color={props.color || "secondary"}
      variant="contained"
      style={{ width: "max-content", fontSize: "12px" }}
    >
      {props.name}
    </Button>
  );
};

const RenderCTAButtons = () => {
  const refresh = useRefresh();
  const record = useRecordContext();
  const [assignDialog, setAssignDialog] = useState(false);
  const [customerIdsInput, setCustomerIdsInput] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  const [removeDialog, setRemoveDialog] = useState(false);
  const [removeIdsInput, setRemoveIdsInput] = useState('');
  const [removeLoading, setRemoveLoading] = useState(false);

  const parseIds = (input: string) =>
    input
      .split(/[\s,]+/)
      .map((s) => Number(s.trim()))
      .filter((n) => !isNaN(n) && n > 0);

  const handleAssign = async () => {
    const ids = parseIds(customerIdsInput);
    if (ids.length === 0) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: 'Please enter at least one valid customer ID',
        type: CustomSnackbarTypes.ERROR,
      });
      return;
    }
    setAssignLoading(true);
    try {
      await couponSkuService.assignCustomersToSku(Number(record?.id), ids);
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: 'Customers assigned successfully',
        type: CustomSnackbarTypes.SUCCESS,
      });
      setAssignDialog(false);
      setCustomerIdsInput('');
    } catch {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: 'Failed to assign customers',
        type: CustomSnackbarTypes.ERROR,
      });
    } finally {
      setAssignLoading(false);
    }
  };

  const handleRemove = async () => {
    const ids = parseIds(removeIdsInput);
    if (ids.length === 0) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: 'Please enter at least one valid customer ID',
        type: CustomSnackbarTypes.ERROR,
      });
      return;
    }
    setRemoveLoading(true);
    try {
      await couponSkuService.assignCustomersToSku(null as any, ids);
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: 'Customers removed from SKU successfully',
        type: CustomSnackbarTypes.SUCCESS,
      });
      setRemoveDialog(false);
      setRemoveIdsInput('');
    } catch {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: 'Failed to remove customers',
        type: CustomSnackbarTypes.ERROR,
      });
    } finally {
      setRemoveLoading(false);
    }
  };

  return (
    <>
      <Stack direction="row" gap={2} sx={{ mb: 1 }}>
        <EditButton />
        {record?.isActive && (
          <CustomButton
            name="Deactivate Sku"
            color="error"
            callback={() => {
              EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
                message: "Are you sure you want deactivate this coupon sku?",
                successLabel: "Confirm",
                failureLabel: "Cancel",
                onClose: (isConfirmed: boolean) => {
                  if (isConfirmed) {
                    couponSkuService.deactivateCouponSku(Number(record?.id)).then(() => {
                      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                        message: "Successful",
                        type: CustomSnackbarTypes.SUCCESS,
                      });
                      refresh();
                    });
                  }
                },
              });
            }}
          />
        )}
        {!record?.isActive && (
          <CustomButton
            name="Activate Sku"
            color="primary"
            callback={() => {
              EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
                message: "Are you sure you want activate this coupon sku?",
                successLabel: "Confirm",
                failureLabel: "Cancel",
                onClose: (isConfirmed: boolean) => {
                  if (isConfirmed) {
                    couponSkuService.activateCouponSku(Number(record?.id)).then(() => {
                      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                        message: "Successful",
                        type: CustomSnackbarTypes.SUCCESS,
                      });
                      refresh();
                    });
                  }
                },
              });
            }}
          />
        )}
        <Button
          size="small"
          color="info"
          variant="contained"
          style={{ width: 'max-content', fontSize: '12px' }}
          onClick={() => setAssignDialog(true)}
        >
          Assign Customers to SKU
        </Button>
        <Button
          size="small"
          color="error"
          variant="contained"
          style={{ width: 'max-content', fontSize: '12px' }}
          onClick={() => setRemoveDialog(true)}
        >
          Remove Customers from SKU
        </Button>
      </Stack>

      <Dialog
        open={assignDialog}
        onClose={() => { if (!assignLoading) { setAssignDialog(false); setCustomerIdsInput(''); } }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Assign Customers to SKU</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Enter customer IDs separated by commas or new lines.
          </Typography>
          <TextField
            label="Customer IDs"
            value={customerIdsInput}
            onChange={(e) => setCustomerIdsInput(e.target.value)}
            fullWidth
            multiline
            rows={4}
            placeholder="e.g. 1302901, 1562821"
            size="small"
            disabled={assignLoading}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => { setAssignDialog(false); setCustomerIdsInput(''); }}
            disabled={assignLoading}
            color="inherit"
            variant="outlined"
            size="small"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="info"
            size="small"
            onClick={handleAssign}
            disabled={assignLoading || !customerIdsInput.trim()}
            startIcon={assignLoading ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {assignLoading ? 'Assigning…' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={removeDialog}
        onClose={() => { if (!removeLoading) { setRemoveDialog(false); setRemoveIdsInput(''); } }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Remove Customers from SKU</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Enter customer IDs to remove from this SKU, separated by commas or new lines.
          </Typography>
          <TextField
            label="Customer IDs"
            value={removeIdsInput}
            onChange={(e) => setRemoveIdsInput(e.target.value)}
            fullWidth
            multiline
            rows={4}
            placeholder="e.g. 1302901, 1562821"
            size="small"
            disabled={removeLoading}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => { setRemoveDialog(false); setRemoveIdsInput(''); }}
            disabled={removeLoading}
            color="inherit"
            variant="outlined"
            size="small"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={handleRemove}
            disabled={removeLoading || !removeIdsInput.trim()}
            startIcon={removeLoading ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {removeLoading ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const CouponSkuShow = (props: any) => {
  return (
    <WorkspaceContainer>
      <Show
        {...props}
        actions={false}
        sx={{
          "& .RaShow-card": {
            boxShadow: "none",
            border: "none",
            backgroundColor: "transparent",
            padding: 0,
          },
        }}
      >
        <RenderCTAButtons />
        <TabbedShowLayout
          sx={{
            "& .RaTabbedShowLayout-content": {
              backgroundColor: "transparent",
              padding: 0,
            },
          }}
        >
          <Tab label="Details">
            <CouponSkuMetaShow />
          </Tab>
        </TabbedShowLayout>
      </Show>
    </WorkspaceContainer>
  );
};

export default CouponSkuShow;
